import React, { useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import styles from './PulseDemo.module.css';

const PLANS = [
  {
    id: 'brunch',
    title: 'Sunday Brunch',
    time: 'Sat · 11:00 AM',
    privacy: 'public',
    joined: 18,
    capacity: 25,
    friends: 4,
    vibe: 'Open brunch - anyone can join.',
    category: 'Brunch',
    color: '#2f9e6b',
  },
  {
    id: 'dinner',
    title: 'Friday Dinner',
    time: 'Tonight · 8:00 PM',
    privacy: 'private',
    joined: 12,
    capacity: 15,
    friends: 3,
    vibe: 'Request to join - host approves.',
    category: 'Dinner',
    color: '#6b7280',
  },
  {
    id: 'girls',
    title: 'Girls night',
    time: 'Sat · 9:00 PM',
    privacy: 'invite',
    joined: 6,
    capacity: 8,
    friends: 2,
    vibe: 'Invite only - share with friends.',
    category: 'Girls night',
    color: '#8b5cf6',
  },
  {
    id: 'bike',
    title: 'Morning bike ride',
    time: 'Sun · 6:00 AM',
    privacy: 'public',
    joined: 9,
    capacity: 15,
    friends: 1,
    vibe: 'Easy ride, all paces welcome.',
    category: 'Bike ride',
    color: '#3b82f6',
  },
  {
    id: 'movie',
    title: 'Late Night Screening',
    time: 'Fri · 10:15 PM',
    privacy: 'invite',
    joined: 3,
    capacity: 5,
    friends: 3,
    vibe: 'Friends-only movie night.',
    category: 'Movie',
    color: '#6366f1',
  },
];

const PRIVACY_META = {
  public: { label: 'Public', hint: 'Anyone can see and join' },
  private: { label: 'Private', hint: 'Anyone can request, you approve or reject' },
  invite: { label: 'Invite only', hint: 'Share with friends, only they can see it' },
};

function AvatarStack({ count = 4 }) {
  const srcs = [
    '/avatars/avatar-1.png',
    '/avatars/avatar-2.png',
    '/avatars/avatar-3.png',
    '/avatars/avatar-4.png',
  ];
  return (
    <div className={styles.avatars} aria-hidden="true">
      {Array.from({ length: count }).map((_, i) => (
        <img
          key={i}
          src={srcs[i % srcs.length]}
          alt=""
          className={styles.avatar}
          style={{ zIndex: count - i }}
        />
      ))}
    </div>
  );
}

function PrivacyBadge({ privacy }) {
  const meta = PRIVACY_META[privacy] || PRIVACY_META.invite;
  return (
    <span className={`${styles.privacyBadge} ${styles[`privacy_${privacy}`] || styles.privacy_invite}`}>
      ✦ {meta.label}
    </span>
  );
}

export default function PulseDemo() {
  const [filter, setFilter] = useState('all');
  const [selectedId, setSelectedId] = useState(PLANS[0].id);
  const [joinedIds, setJoinedIds] = useState(() => new Set());

  const plans = useMemo(() => {
    if (filter === 'all') return PLANS;
    return PLANS.filter((p) => p.privacy === filter);
  }, [filter]);

  const selected = plans.find((p) => p.id === selectedId) || plans[0] || PLANS[0];

  const handleJoin = (plan) => {
    setJoinedIds((prev) => {
      const next = new Set(prev);
      if (next.has(plan.id)) next.delete(plan.id);
      else next.add(plan.id);
      return next;
    });
  };

  const isJoined = (id) => joinedIds.has(id);
  const joinedCount = (plan) => plan.joined + (isJoined(plan.id) ? 1 : 0);

  return (
    <div className={styles.demo}>
      <div className={styles.feedCol}>
        <div className={styles.feedHeader}>
          <div>
            <div className={styles.liveDotRow}>
              <span className={styles.liveDot} />
              Plans
            </div>
            <p className={styles.feedSub}>
              Public, private, or invite only - create the plan, invite people, then keep the memories.
            </p>
          </div>
        </div>

        <div className={styles.filters} role="tablist" aria-label="Filter plans by privacy">
          {[
            { id: 'all', label: 'All' },
            { id: 'public', label: 'Public' },
            { id: 'private', label: 'Private' },
            { id: 'invite', label: 'Invite only' },
          ].map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={filter === tab.id}
              className={`${styles.filterBtn} ${filter === tab.id ? styles.filterActive : ''}`}
              onClick={() => {
                setFilter(tab.id);
                const first = tab.id === 'all' ? PLANS[0] : PLANS.find((p) => p.privacy === tab.id);
                if (first) setSelectedId(first.id);
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        <div className={styles.planList}>
          <AnimatePresence mode="popLayout">
            {plans.map((plan) => {
              const count = joinedCount(plan);
              const pct = Math.min(100, Math.round((count / plan.capacity) * 100));
              const active = selected?.id === plan.id;
              return (
                <motion.button
                  key={plan.id}
                  type="button"
                  layout
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, scale: 0.96 }}
                  transition={{ duration: 0.28 }}
                  className={`${styles.planCard} ${active ? styles.planCardActive : ''}`}
                  onClick={() => setSelectedId(plan.id)}
                >
                  <div className={styles.planTop}>
                    <span className={styles.catDot} style={{ background: plan.color }} />
                    <div className={styles.planMeta}>
                      <div className={styles.planTitle}>{plan.title}</div>
                      <div className={styles.planPlace}>{plan.time}</div>
                    </div>
                    <PrivacyBadge privacy={plan.privacy} />
                  </div>
                  <div className={styles.progressRow}>
                    <div className={styles.progressTrack}>
                      <motion.div
                        className={styles.progressFill}
                        animate={{ width: `${pct}%` }}
                        transition={{ type: 'spring', stiffness: 120, damping: 18 }}
                      />
                    </div>
                    <span className={styles.progressLabel}>{count}/{plan.capacity}</span>
                  </div>
                </motion.button>
              );
            })}
          </AnimatePresence>
        </div>
      </div>

      <div className={styles.detailCol}>
        <AnimatePresence mode="wait">
          {selected ? (
            <motion.div
              key={selected.id}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.28 }}
              className={styles.detailCard}
            >
              <div className={styles.detailHero} style={{ background: `linear-gradient(145deg, ${selected.color}55, transparent 60%), var(--color-surface-alt)` }}>
                <PrivacyBadge privacy={selected.privacy} />
                <div className={styles.detailCat}>{selected.category}</div>
              </div>
              <div className={styles.detailBody}>
                <h3 className={styles.detailTitle}>{selected.title}</h3>
                <p className={styles.detailVibe}>{selected.vibe}</p>
                <div className={styles.detailTime}>🕒 {selected.time}</div>
                <p className={styles.memoryHint}>{PRIVACY_META[selected.privacy]?.hint}</p>

                <div className={styles.socialRow}>
                  <AvatarStack count={4} />
                  <span>
                    {joinedCount(selected)} going
                    {selected.friends ? ` · ${selected.friends} friends` : ''}
                  </span>
                </div>

                <div className={styles.progressRow}>
                  <div className={styles.progressTrack}>
                    <motion.div
                      className={styles.progressFill}
                      animate={{ width: `${Math.min(100, Math.round((joinedCount(selected) / selected.capacity) * 100))}%` }}
                      transition={{ type: 'spring', stiffness: 120, damping: 18 }}
                    />
                  </div>
                  <span className={styles.progressLabel}>
                    {joinedCount(selected)} / {selected.capacity} joined
                  </span>
                </div>

                <button
                  type="button"
                  className={`${styles.joinBtn} ${isJoined(selected.id) ? styles.joinBtnDone : ''}`}
                  onClick={() => handleJoin(selected)}
                >
                  {isJoined(selected.id)
                    ? 'Joined ✓'
                    : selected.privacy === 'private'
                      ? 'Request to join'
                      : selected.privacy === 'invite'
                        ? 'Invite only'
                        : 'Join Plan'}
                </button>
                <p className={styles.memoryHint}>
                  After the plan, add event photos. They become a memory on your timeline.
                </p>
              </div>
            </motion.div>
          ) : null}
        </AnimatePresence>
      </div>
    </div>
  );
}
